import { API_URL } from './constants';

const DEFAULT_TIMEOUT = 3000;

interface fetchProps {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  params?: { [x: string]: string };
  timeout?: number;
  body?: any;
}

type RESPONSE_CODES =
  | 'OK' // 200
  | 'CREATED' // 201
  | 'BAD_REQUEST' // 400
  | 'UNAUTHENTICATED' // 401
  | 'FORBIDDEN' // 403
  | 'NOT_FOUND' // 404
  | 'FAILED' // 500
  | 'CRASHED'; // 503

// const RESPONSE_CODES_MAP = {
//   "OK": 200,
//   "CREATED": 201,
//   "BAD_REQUEST": 400,
//   "UNAUTHENTICATED": 401,
//   "FORBIDDEN": 403,
//   "NOT_FOUND": 404,
//   "FAILED": 500,
//   "CRASHED": 503,
// }

export interface ApiResponse {
  status: RESPONSE_CODES;
  message?: string;
  data?: any;
}
const fetchHelper = async ({
  url: urlString,
  method,
  params,
  timeout = DEFAULT_TIMEOUT,
  body,
  ...rest
}: fetchProps) => {
  const abortController = new AbortController();
  let isTimeout = false;

  /* eslint-disable-next-line */
  const config: RequestInit = {
    credentials: 'include',
    method,
    headers: {
      'Content-type': 'application/json'
    },
    body: JSON.stringify(body),
    ...rest
  };

  const url = createUrl(urlString, params);
  // const r = new Request(url.toString(), config)

  const timeoutHandle = setTimeout(() => {
    isTimeout = true;
    abortController.abort();
  }, timeout);

  // if (signal) {
  //   signal.addEventListener('abort', () => {
  //     abortController.abort();
  //     clearTimeout(timeoutHandle);
  //   });
  // }

  return await fetch(url, config)
    .catch((e) => {
      if (isTimeout) {
        throw createTimeoutError(config);
      }

      if (e.name === 'AbortError') {
        throw e;
      }

      // Assume any other error is offline error.
      throw createOfflineError(config);
    })
    .then((response) => {
      clearTimeout(timeoutHandle);
      return checkStatus(response, config);
    })
    .then((data) => data);
};

export const createUrl = (urlString: string, params: { [key: string]: any } = {}) => {
  let url: URL;
  url = new URL(`${API_URL}${urlString}`);
  appendQueryParams(url, params);
  return url;
};

export const createTimeoutError = (config: any) => {
  const error = new ApiError('Request timed out', CUSTOM_FETCH_ERROR_STATUS_CODE.TIMEOUT, 'TimeoutError');
  error.config = config;
  return error;
};

export enum CUSTOM_FETCH_ERROR_STATUS_CODE {
  NO_NETWORK_CONNECTION = 0,
  TIMEOUT = -1
}

export const createOfflineError = (config: any) => {
  const error = new ApiError(
    'No network connection',
    CUSTOM_FETCH_ERROR_STATUS_CODE.NO_NETWORK_CONNECTION,
    'OfflineError'
  );
  error.config = config;
  return error;
};

export class ApiError extends Error {
  response?: Response;

  status: number;

  data?: any;

  config: any;

  constructor(message: string, status: number, name: string) {
    super(message);
    Object.setPrototypeOf(this, ApiError.prototype);
    this.status = status;
    this.name = name;
  }
}

const appendQueryParams = (url: URL, params: { [key: string]: any }) => {
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (typeof value === 'undefined') {
      return;
    }
    url.searchParams.append(key, value);
  });
};

export const checkStatus = (response: Response, config: any): Promise<ApiResponse> => {
  const { status } = response;

  if (status >= 200 && status < 300) {
    return response.json();
  }

  return response
    .json()
    .catch((err) => {
      return err;
    })
    .then((data) => {
      if (data.status === "OK") {
        return data;
      } else {
        throw createApiError('StatusCodeError', response, config, data);
      }
    });
};

export const createApiError = (name: string, response: Response, config: any, data?: any) => {
  const { statusText, status } = response;

  const error = new ApiError(statusText, status, name);

  error.response = response;
  error.data = data;
  error.config = config;

  return error;
};

interface Props {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: any;
  params?: { [x: string]: string };
}

export default async function xhr({ url, method, params, body, ...config }: Props) {
  // const { headers: dataHeaders, body } = serializeData(data, input);
  return await fetchHelper({
    url,
    params,
    body,
    method,
    ...config
  });
}
